import { PAYMENT_MODULE } from "@/api_framework/api_config";
import {
  IInvoiceAdjustmentRequest,
  IInvoiceAdjustmentSummary,
  IInvoiceAdjustmentUploadPayload,
} from "@/api_framework/api_modals/payment";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback } from "react";
import { useAPIUtils } from "../../useAPIUtils";

AxiosCurlirize(axios, () => {});

interface IAPIResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

export type IInvoiceAdjustmentUploadResult = {
  status: boolean;
  request: IInvoiceAdjustmentRequest | null;
  // On a validation failure the API sends the refreshed summary back
  summary: IInvoiceAdjustmentSummary | null;
};

// Raises the request: force discount starts processing, bad debt goes to finance
export const useInvoiceAdjustmentUploadAPI = () => {
  const configuration = useConfiguration();
  const { captureError, toastNotification } = useAPIUtils();
  const { sendCurl } = useCurlLogger();

  const execute = useCallback(
    async (payload: IInvoiceAdjustmentUploadPayload): Promise<IInvoiceAdjustmentUploadResult> => {
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        return await axios
          .post(PAYMENT_MODULE?.INVOICE_ADJUSTMENT_UPLOAD?.baseURL ?? "", payload, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<IAPIResponse<IInvoiceAdjustmentRequest>>): IInvoiceAdjustmentUploadResult => {
            if (res.data.status) {
              toastNotification(res.data.message);
              return { status: true, request: res.data.data, summary: null };
            }
            return { status: false, request: null, summary: null };
          })
          .catch((e: AxiosError<IAPIResponse<IInvoiceAdjustmentSummary>>): IInvoiceAdjustmentUploadResult => {
            sendCurl(e.config?.["curlCommand"]);
            captureError(e);
            const summary = e.response?.data?.data;
            return { status: false, request: null, summary: summary && "invoices" in summary ? summary : null };
          });
      } catch (e) {
        captureError(e as AxiosError);
        return { status: false, request: null, summary: null };
      }
    },
    [captureError, configuration.localStorage.ACCESS_TOKEN.key, sendCurl, toastNotification],
  );

  return { execute };
};
